import { useEffect, useRef } from "react";
import { api } from "./api";

/**
 * useIdleLogout — logs the user out after `idleMs` of no activity
 * and pings the backend every `pingMs` so admins can see last-active time.
 * Activity = mousemove, keydown, click, scroll, touchstart.
 */
// `logoutEnabled` = CRM Access per-user "Auto logout after 15 min" toggle.
export function useIdleLogout({ enabled, logoutEnabled, onIdle, idleMs = 15 * 60 * 1000, pingMs = 60 * 1000 }) {
  const lastActivity = useRef(Date.now());
  // Keep the latest callback without re-running the effect (re-runs used to fire extra pings).
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return;

    const bump = () => { lastActivity.current = Date.now(); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel", "click"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    // Fire a ping right away so last_active_at reflects login
    api.post("/users/ping").catch(() => {});

    const idleCheck = setInterval(() => {
      if (logoutEnabled && Date.now() - lastActivity.current >= idleMs) {
        clearInterval(idleCheck);
        clearInterval(pinger);
        onIdleRef.current?.();
      }
    }, 15 * 1000); // check every 15s

    const pinger = setInterval(() => {
      // Only ping if the user was active within the last `pingMs` window
      // (mouse / key / scroll / click / nav). This is what makes the backend
      // "delta ≤ 5 min → count" logic correctly EXCLUDE idle stretches: if
      // the user goes idle, we stop pinging, so the next ping (whenever they
      // come back) will have delta > 5 min and credit 0 seconds for the gap.
      if (Date.now() - lastActivity.current < pingMs) {
        api.post("/users/ping").catch(() => {});
      }
    }, pingMs);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(idleCheck);
      clearInterval(pinger);
    };
  }, [enabled, logoutEnabled, idleMs, pingMs]);
}
